import React from "react";
import { Autocomplete, Box, Button, Checkbox, GlobalStyles, IconButton, InputBase, TextField } from "@mui/material";

export const COLORS = {
  gold1: "#F6C445",
  gold2: "#EBA92B",
  goldDeep: "#C8871A",
  emerald: "#1A7F37",
  emeraldDark: "#136128",
  emeraldSoft: "#E8F3EC",
  ink: "#1C2A21",
  muted: "#6E7B72",
  card: "#FFFFFF",
  page: "#FBF7EE",
  line: "#EFE7D6",
  amberWarn: "#E8A317",
  amberSoft: "#FDF3DE",
};

export const SHADOW = "0 6px 20px rgba(120,90,20,.10)";
export const SHADOW_SM = "0 2px 8px rgba(120,90,20,.08)";
export const DISPLAY_FONT = "'Baloo 2', sans-serif";
export const BODY_FONT = "'Mukta', system-ui, sans-serif";

export function ensureMandiFontsLoaded() {
  if (document.getElementById("mandi-report-fonts")) return;

  const pre1 = document.createElement("link");
  pre1.rel = "preconnect";
  pre1.href = "https://fonts.googleapis.com";

  const pre2 = document.createElement("link");
  pre2.rel = "preconnect";
  pre2.href = "https://fonts.gstatic.com";
  pre2.crossOrigin = "anonymous";

  const fontLink = document.createElement("link");
  fontLink.id = "mandi-report-fonts";
  fontLink.rel = "stylesheet";
  fontLink.href =
    "https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700;800&family=Mukta:wght@400;500;600;700&display=swap";

  document.head.appendChild(pre1);
  document.head.appendChild(pre2);
  document.head.appendChild(fontLink);
}

export function MandiGlobalStyles() {
  return (
    <GlobalStyles
      styles={{
        "html, body": { margin: 0, padding: 0, height: "100%" },
        body: { overflow: "hidden", position: "fixed", inset: 0 },
        "@keyframes mandiSlide": {
          from: { opacity: 0, transform: "translateX(16px)" },
          to: { opacity: 1, transform: "translateX(0)" },
        },
        "@keyframes mandiPop": {
          from: { transform: "scale(.4)", opacity: 0 },
          to: { transform: "scale(1)", opacity: 1 },
        },
      }}
    />
  );
}

export const PRIMARY_BTN_SX = {
  width: "100%",
  border: "none",
  borderRadius: "16px",
  background: COLORS.emerald,
  color: "#fff",
  fontFamily: DISPLAY_FONT,
  fontWeight: 700,
  fontSize: 19,
  padding: "16px",
  boxShadow: "0 8px 20px rgba(26,127,55,.32)",
  textTransform: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  transition: "transform .12s, background .2s",
  "&:hover": { background: COLORS.emerald },
  "&:active": { transform: "scale(.97)" },
  "&.Mui-disabled": { color: "#fff", background: COLORS.emerald, opacity: 0.5 },
};

export const GHOST_BTN_SX = {
  ...PRIMARY_BTN_SX,
  background: "#fff",
  color: COLORS.emerald,
  border: `2px solid ${COLORS.emeraldSoft}`,
  boxShadow: SHADOW_SM,
  "&:hover": { background: "#fff" },
  "&.Mui-disabled": { color: COLORS.emerald, background: "#fff", opacity: 0.5 },
};

export const focusInput = (e) => {
  const input = e.currentTarget.querySelector("input");
  if (input) input.focus();
};

export function TileInput({ label, value, onChange, placeholder = "0", error }) {
  return (
    <Box
      onClick={focusInput}
      sx={{
        background: COLORS.page,
        border: `2px solid ${error ? COLORS.amberWarn : "transparent"}`,
        borderRadius: "13px",
        padding: "9px 10px",
        cursor: "text",
        transition: "border-color .2s",
        "&:focus-within": { borderColor: error ? COLORS.amberWarn : COLORS.emerald, background: "#fff" },
      }}
    >
      <Box
        component="label"
        sx={{ display: "block", fontSize: 11, color: COLORS.muted, fontWeight: 600, lineHeight: 1.2 }}
      >
        {label}
      </Box>
      <InputBase
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        inputProps={{ inputMode: "numeric", enterKeyHint: "next" }}
        sx={{
          fontFamily: DISPLAY_FONT,
          fontWeight: 700,
          fontSize: 23,
          color: error ? COLORS.amberWarn : COLORS.emerald,
          width: "100%",
          "& input": { padding: "2px 0 0" },
          "& input::placeholder": { color: "#D2DAD2", opacity: 1 },
        }}
      />
    </Box>
  );
}

export function TileInputRow({
  qtyLabel = "आवक",
  loLabel = "लो रेट ₹",
  hiLabel = "हाई रेट ₹",
  qty,
  lo,
  hi,
  onQtyChange,
  onLoChange,
  onHiChange,
  error, 
}) {
  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "9px" }}>
        <TileInput label={qtyLabel} value={qty} onChange={onQtyChange} />
        <TileInput label={loLabel} value={lo} onChange={onLoChange} error={!!error} />
        <TileInput label={hiLabel} value={hi} onChange={onHiChange} error={!!error} />
      </Box>
      {error && (
        <Box
          sx={{
            fontSize: 12,
            color: COLORS.amberWarn,
            fontWeight: 600,
            mt: "6px",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          ⚠ {error}
        </Box>
      )}
    </Box>
  );
}

export function FlatAutocomplete({ options, value, onChange, placeholder }) {
  return (
    <Autocomplete
      fullWidth
      options={options}
      value={value || null}
      onChange={(_, newValue) => onChange(newValue || "")}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder={placeholder}
          InputProps={{ ...params.InputProps, disableUnderline: true }}
        />
      )}
      sx={{
        "& .MuiInputBase-root": {
          padding: "2px 0 !important",
          fontFamily: DISPLAY_FONT,
          fontWeight: 600,
          fontSize: 16,
          color: COLORS.ink,
        },
        "& .MuiAutocomplete-input": { padding: "6px 0 !important" },
      }}
    />
  );
}

export function ScreenHeader({ title, subtitle, showBack, onBack, stepCount, activeStep }) {
  return (
    <Box
      sx={{
        position: "relative",
        flex: "none",
        color: "#3a2c05",
        background: `linear-gradient(150deg, ${COLORS.gold1}, ${COLORS.gold2} 70%, ${COLORS.goldDeep})`,
        padding: "10px 18px 12px",
        borderRadius: "0 0 20px 20px",
        boxShadow: "0 8px 22px rgba(200,135,26,.28)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          right: -16,
          top: -18,
          fontSize: 100,
          opacity: 0.16,
          transform: "rotate(12deg)",
          pointerEvents: "none",
        }}
      >
        🌾
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minHeight: 28 }}>
        <IconButton
          onClick={onBack}
          disableRipple
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: "rgba(255,255,255,.55)",
            color: "#5a4406",
            fontWeight: 700,
            fontSize: 18,
            visibility: showBack ? "visible" : "hidden",
            p: 0,
            flex: "none",
            "&:hover": { background: "rgba(255,255,255,.55)" },
          }}
        >
          ‹
        </IconButton>

        <Box
          component="h1"
          sx={{
            fontFamily: DISPLAY_FONT,
            fontWeight: 800,
            margin: 0,
            fontSize: 21,
            lineHeight: 1.1,
            letterSpacing: "0.2px",
          }}
        >
          {title}
        </Box>
      </Box>

      <Box
        sx={{
          fontSize: "10.5px",
          fontWeight: 600,
          letterSpacing: "1.5px",
          textTransform: "uppercase",
          opacity: 0.7,
          marginTop: "4px",
          marginLeft: "38px",
        }}
      >
        {subtitle}
      </Box>

      <Box sx={{ display: "flex", gap: "6px", marginTop: "9px" }}>
        {Array.from({ length: stepCount }).map((_, i) => (
          <Box
            key={i}
            sx={{
              height: 4,
              borderRadius: "3px",
              background: i <= activeStep ? "#fff" : "rgba(255,255,255,.45)",
              flex: 1,
              transition: ".3s",
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

export function ScreenFooter({ children }) {
  return (
    <Box
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        padding: "14px 16px calc(14px + env(safe-area-inset-bottom))",
        background: `linear-gradient(to top, ${COLORS.page} 68%, rgba(251,247,238,0))`,
      }}
    >
      {children}
    </Box>
  );
}

export function SummaryRow({ label, sublabel, value, onClick, editable = false }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        background: COLORS.card,
        border: `1px solid ${COLORS.line}`,
        borderRadius: "14px",
        padding: "16px 16px",
        mb: "10px",
        fontSize: 15,
        transition: ".15s",
        "&:active": onClick ? { background: COLORS.page } : undefined,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, pr: "10px" }}>
        <Box
          component="span"
          sx={{
            fontWeight: 800,          
            color: COLORS.ink,
            fontSize: 19,             
            display: "block",
            lineHeight: 1.4,
          }}
        >
          {label}
        </Box>
        {sublabel && (
          <Box sx={{ fontSize: 13, color: COLORS.muted, mt: "4px", lineHeight: 1.45 }}>
            {sublabel}
          </Box>
        )}
      </Box>
      <Box sx={{ textAlign: "right", flex: "none" }}>
        <Box
          component="b"
          sx={{
            fontFamily: DISPLAY_FONT,
            color: COLORS.emerald,
            whiteSpace: "nowrap",
            fontSize: 21,
            fontWeight: 700,
          }}
        >
          {value}
        </Box>
        {editable && (
          <Box
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: COLORS.emerald,
              mt: "3px",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "3px",
            }}
          >
            ✎ बदलें
          </Box>
        )}
      </Box>
    </Box>
  );
}

export function SelectableTile({ label, selected, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        minWidth: 0,
        background: selected ? "#FFF9EC" : COLORS.card,
        border: `2px solid ${selected ? COLORS.gold2 : COLORS.line}`,
        borderRadius: "14px",
        padding: "11px 12px",
        boxShadow: SHADOW_SM,
        transition: ".18s",
        cursor: "pointer",
      }}
    >
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          border: `2px solid ${selected ? COLORS.gold2 : "#DCD3C0"}`,
          background: selected ? COLORS.gold2 : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: 15,
          flex: "none",
          transition: ".18s",
        }}
      >
        ✓
      </Box>
      <Box
        sx={{
          fontFamily: DISPLAY_FONT,
          fontWeight: 700,
          fontSize: 17,
          color: COLORS.emeraldDark,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
      >
        {label}
      </Box>
    </Box>
  );
}

export function ArrivalMeterCard({ totalMarket, totalSum, meterPct, isOk, diff, diffPercent }) {
  return (
    <Box
      sx={{
        background: COLORS.card,
        borderRadius: "20px",
        padding: "16px 16px 14px",
        boxShadow: SHADOW,
        border: `1px solid ${COLORS.line}`,
        mb: "16px",
      }}
    >
      <Box sx={{ fontWeight: 700, color: COLORS.emeraldDark, fontSize: 14, letterSpacing: "0.3px" }}>
        आवक मिलान{" "}
        <Box component="span" sx={{ color: COLORS.muted, fontWeight: 600, fontSize: 12 }}>
          Arrival check
        </Box>
      </Box>
      <Box sx={{ fontSize: "12.5px", color: COLORS.muted, margin: "3px 0 12px", lineHeight: 1.35 }}>
        कुल आवक और चुनी हुई वैरायटी की आवक का मिलान। 5% तक अंतर ठीक है।
      </Box>
      <Box sx={{ display: "flex", gap: "10px" }}>
        <Box sx={{ flex: 1, background: COLORS.page, borderRadius: "14px", padding: "10px 12px" }}>
          <Box component="span" sx={{ fontSize: "11.5px", color: COLORS.muted }}>
            कुल (Total)
          </Box>
          <Box
            sx={{
              display: "block",
              fontFamily: DISPLAY_FONT,
              fontSize: 22,
              color: COLORS.emerald,
              fontWeight: 700,
              lineHeight: 1.1,
              mt: "2px",
            }}
          >
            {totalMarket.toLocaleString("en-IN")}
          </Box>
        </Box>
        <Box sx={{ flex: 1, background: COLORS.page, borderRadius: "14px", padding: "10px 12px" }}>
          <Box component="span" sx={{ fontSize: "11.5px", color: COLORS.muted }}>
            वैरायटी (Sum)
          </Box>
          <Box
            sx={{
              display: "block",
              fontFamily: DISPLAY_FONT,
              fontSize: 22,
              color: COLORS.emerald,
              fontWeight: 700,
              lineHeight: 1.1,
              mt: "2px",
            }}
          >
            {totalSum.toLocaleString("en-IN")}
          </Box>
        </Box>
      </Box>
      <Box sx={{ height: 8, borderRadius: "6px", background: "#EEE6D6", mt: "12px", overflow: "hidden" }}>
        <Box
          sx={{
            display: "block",
            height: "100%",
            width: `${meterPct}%`,
            background: isOk ? COLORS.emerald : COLORS.amberWarn,
            transition: "width .4s, background .4s",
          }}
        />
      </Box>
      <Box
        sx={{
          mt: "9px",
          fontWeight: 700,
          fontSize: "13.5px",
          color: isOk ? COLORS.emerald : COLORS.amberWarn,
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        {isOk
          ? "✓ मिलान सही है"
          : `⚠ अभी ${diff.toLocaleString("en-IN")} बोरी (${diffPercent.toFixed(1)}%) का अंतर है`}
      </Box>
    </Box>
  );
}

export function MismatchWarningBanner({ totalMarket, totalSum, diffPercent }) {
  return (
    <Box
      sx={{
        background: COLORS.amberSoft,
        border: `1.5px solid ${COLORS.amberWarn}`,
        borderRadius: "14px",
        padding: "12px 14px",
        mb: "14px",
      }}
    >
      <Box
        sx={{
          fontWeight: 700,
          fontSize: "13.5px",
          color: COLORS.amberWarn,
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        ⚠ आवक का मिलान नहीं हो रहा
      </Box>
      <Box sx={{ fontSize: 12.5, color: COLORS.ink, mt: "4px", lineHeight: 1.5 }}>
        पूरी मंडी आवक ({totalMarket.toLocaleString("en-IN")} बोरी) और सभी वैरायटी के योग (
        {totalSum.toLocaleString("en-IN")} बोरी) में <Box component="b">{diffPercent.toFixed(1)}%</Box> का
        अंतर है — सिर्फ़ 5% तक अंतर की अनुमति है। कृपया नीचे किसी भी वैरायटी कार्ड पर टैप करके मात्रा ठीक
        करें, या "पूरी मंडी आवक" बदलें।
      </Box>
    </Box>
  );
}

export function DuplicateReportBanner({ message }) {
  return (
    <Box
      sx={{
        background: COLORS.amberSoft,
        border: `1.5px solid ${COLORS.amberWarn}`,
        borderRadius: "14px",
        padding: "14px 16px",
        mb: "14px",
        textAlign: "center",
      }}
    >
      <Box sx={{ fontSize: 30, mb: "6px" }}>⚠</Box>
      <Box
        sx={{
          fontWeight: 700,
          fontSize: "15px",
          color: COLORS.amberWarn,
          mb: "4px",
        }}
      >
        रिपोर्ट पहले से मौजूद है
      </Box>
      <Box sx={{ fontSize: 13, color: COLORS.ink, lineHeight: 1.5 }}>
        {message ||
          "इस मंडी पार्टनर के लिए आज की तारीख़ की रिपोर्ट पहले से ही भेजी जा चुकी है। कृपया दोबारा रिपोर्ट न भेजें।"}
      </Box>
    </Box>
  );
}

export function EmptyStateCard({ message, actionLabel, onAction }) {
  return (
    <Box
      sx={{
        background: COLORS.amberSoft,
        border: `1.5px solid ${COLORS.amberWarn}`,
        borderRadius: "14px",
        padding: "14px",
        textAlign: "center",
      }}
    >
      <Box sx={{ fontSize: 13.5, color: COLORS.ink, mb: onAction ? "10px" : 0 }}>{message}</Box>
      {onAction && (
        <Button
          onClick={onAction}
          disableRipple
          sx={{ textTransform: "none", fontFamily: DISPLAY_FONT, fontWeight: 700, color: COLORS.emerald }}
        >
          {actionLabel}
        </Button>
      )}
    </Box>
  );
}

export function BrandLogo({ src, alt = "Armo Agrotech", size = 88 }) {
  const compactSize = size * 0.65;

  if (src) {
    return (
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: size,
          height: size,
          objectFit: "contain",
          mb: "18px",
          "@media (max-height: 700px)": { width: compactSize, height: compactSize, mb: "8px" },
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "26%",
        background: `linear-gradient(150deg, ${COLORS.emerald}, ${COLORS.emeraldDark})`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 10px 26px rgba(19,97,40,.34)",
        mb: "18px",
        padding: "6px",
        textAlign: "center",
        "@media (max-height: 700px)": { width: compactSize, height: compactSize, mb: "8px" },
      }}
    >
      <Box
        sx={{
          fontFamily: DISPLAY_FONT,
          fontWeight: 800,
          color: "#fff",
          fontSize: size * 0.19,
          lineHeight: 1.15,
          "@media (max-height: 700px)": { fontSize: size * 0.13 },
        }}
      >
        ARMO
        <br />
        AGROTECH
      </Box>
    </Box>
  );
}

export function SearchableDropdownField({ label, placeholder, options, value, onChange }) {
  return (
    <Box
      sx={{
        background: COLORS.card,
        border: `2px solid ${COLORS.line}`,
        borderRadius: "18px",
        boxShadow: SHADOW_SM,
        padding: "12px 16px",
        transition: "border-color .2s",
        "&:focus-within": { borderColor: COLORS.emerald },
      }}
    >
      {label && (
        <Box component="label" sx={{ display: "block", fontSize: 12, color: COLORS.muted, fontWeight: 600, mb: "4px" }}>
          {label}
        </Box>
      )}
      <FlatAutocomplete options={options} value={value} onChange={onChange} placeholder={placeholder} />
    </Box>
  );
}

export function MandiCodeSearchField({
  label = "मंडी कोड",
  placeholder = "जैसे: 1001",
  value,
  onChange,
  onSearch,
  loading,
}) {
  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (value && !loading) onSearch();
  };

  return (
    <Box
      sx={{
        background: COLORS.card,
        border: `2px solid ${COLORS.line}`,
        borderRadius: "18px",
        boxShadow: SHADOW_SM,
        padding: "12px 14px",
        display: "flex",
        alignItems: "flex-end",
        gap: "10px",
        transition: "border-color .2s",
        "&:focus-within": { borderColor: COLORS.emerald },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {label && (
          <Box component="label" sx={{ display: "block", fontSize: 12, color: COLORS.muted, fontWeight: 600, mb: "4px" }}>
            {label}
          </Box>
        )}
        <InputBase
          autoFocus
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          inputProps={{ inputMode: "numeric", enterKeyHint: "search" }}
          sx={{
            fontFamily: DISPLAY_FONT,
            fontWeight: 700,
            fontSize: 22,
            color: COLORS.emerald,
            width: "100%",
            "& input": { padding: 0 },
            "& input::placeholder": { color: "#D2DAD2", opacity: 1 },
          }}
        />
      </Box>
      <Button
        onClick={onSearch}
        disableElevation
        disabled={!value || loading}
        sx={{
          flex: "none",
          textTransform: "none",
          fontFamily: DISPLAY_FONT,
          fontWeight: 700,
          fontSize: 14,
          color: "#fff",
          background: COLORS.emerald,
          borderRadius: "12px",
          padding: "10px 16px",
          minWidth: 0,
          boxShadow: "none",
          "&:hover": { background: COLORS.emerald },
          "&.Mui-disabled": { color: "#fff", background: COLORS.emerald, opacity: 0.5 },
        }}
      >
        {loading ? "खोज रहे हैं…" : "खोजें"}
      </Button>
    </Box>
  );
}

export function PartnerDetailConfirmCard({ name, location, phone, checked, onToggle }) {
  return (
    <Box
      onClick={() => onToggle(!checked)}
      sx={{
        background: COLORS.card,
        border: `2px solid ${checked ? COLORS.emerald : COLORS.line}`,
        borderRadius: "18px",
        boxShadow: SHADOW_SM,
        padding: "14px 16px",
        mt: "14px",
        width: "100%",
        cursor: "pointer",
        transition: "border-color .18s",
      }}
    >
      <Box sx={{ fontSize: 11, color: COLORS.muted, fontWeight: 600, mb: "8px", letterSpacing: "0.4px" }}>
        चुने गए विवरण की पुष्टि करें
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mb: "10px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box sx={{ fontSize: 16, flex: "none" }}>👤</Box>
          <Box sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 17, color: COLORS.emeraldDark }}>
            {name || "—"}
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box sx={{ fontSize: 16, flex: "none" }}>📍</Box>
          <Box sx={{ fontSize: 14, color: COLORS.ink }}>{location || "—"}</Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box sx={{ fontSize: 16, flex: "none" }}>📞</Box>
          <Box sx={{ fontSize: 14, color: COLORS.ink }}>{phone || "—"}</Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          borderTop: `1px solid ${COLORS.line}`,
          pt: "10px",
        }}
      >
        <Checkbox
          checked={checked}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onToggle(e.target.checked)}
          disableRipple
          sx={{
            p: 0,
            color: COLORS.line,
            "&.Mui-checked": { color: COLORS.emerald },
          }}
        />
        <Box
          sx={{
            fontSize: 13.5,
            fontWeight: 600,
            color: checked ? COLORS.emerald : COLORS.ink,
          }}
        >
          मैंने सही विवरण चुना है
        </Box>
      </Box>
    </Box>
  );
}